import { homeWidgetRegistry } from "@/pages/home/widgets/home-widget.registry.tsx";
import { ApiOrganizationHomeLayoutWidget } from "@/types/organization.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface HomeLayoutAdminEditorProps {
  widgets: ApiOrganizationHomeLayoutWidget[];
  onChange: (widgets: ApiOrganizationHomeLayoutWidget[]) => void;
}

export const HomeLayoutAdminEditor = ({ widgets, onChange }: HomeLayoutAdminEditorProps) => {
  const { t } = useTranslation();

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(widgets);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onChange(reordered);
  };

  const handleField = (
    id: ApiOrganizationHomeLayoutWidget["id"],
    field: "hidden" | "locked",
    value: boolean
  ) => {
    onChange(widgets.map((widget) => (widget.id === id ? { ...widget, [field]: value } : widget)));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={"home-layout-admin"}>
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {widgets.map((widget, index) => {
              const isFixed = widget.is_fixed;

              return (
                <Draggable key={widget.id} draggableId={widget.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        opacity: snapshot.isDragging ? 0.8 : 1,
                      }}
                    >
                      <Stack
                        className={"py-3 border-b border-dark/10"}
                        direction={"horizontal"}
                        gap={"xs"}
                        items={"center"}
                      >
                        <Stack
                          className={"w-8 h-8 rounded-sm hover:bg-dark/5 cursor-move shrink-0"}
                          items={"center"}
                          justify={"center"}
                          {...provided.dragHandleProps}
                        >
                          <FontAwesomeIcon icon={"fa-light fa-grip-dots-vertical"} />
                        </Stack>
                        <Stack
                          className={"grow shrink-0"}
                          direction={"horizontal"}
                          gap={"xs"}
                          items={"center"}
                        >
                          <Text className={"font-semibold"}>
                            {t(homeWidgetRegistry[widget.id].titleKey)}
                          </Text>
                          {!isFixed && widget.locked && (
                            <Tooltip>
                              <TooltipTrigger>
                                <FontAwesomeIcon
                                  icon={"fa-light fa-lock"}
                                  className={"text-brand"}
                                />
                              </TooltipTrigger>
                              <TooltipContent>{t("core:home_layout_locked_hint")}</TooltipContent>
                            </Tooltip>
                          )}
                        </Stack>
                        <Stack
                          className={"shrink-0"}
                          justify={"end"}
                          gap={"sm"}
                          direction={"horizontal"}
                          items={"center"}
                        >
                          {!isFixed && (
                            <>
                              <Button
                                variant={"secondary"}
                                onClick={() => handleField(widget.id, "hidden", !widget.hidden)}
                              >
                                {widget.hidden ? t("common:show") : t("common:hide")}
                              </Button>
                              <DropdownMenu closeOnClick={false}>
                                <DropdownMenuItem
                                  title={t("core:home_layout_lock")}
                                  isSelected={widget.locked}
                                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-lock"} />}
                                  onClick={() => handleField(widget.id, "locked", !widget.locked)}
                                />
                              </DropdownMenu>
                            </>
                          )}
                        </Stack>
                      </Stack>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
